/**
 * A/B Test Export Modal
 *
 * Modal for exporting A/B test results in JSON or Markdown format.
 * Follows the RLLoopExportModal pattern.
 */

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  CompareArrows as CompareIcon,
  EmojiEvents as RankingsIcon,
  Psychology as AnalysisIcon,
  BarChart as StatsIcon
} from '@mui/icons-material';
import { exportABTestResults } from '../../../services/advancedSimulationApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const STATUS_COLORS = {
  draft: 'default',
  running: 'primary',
  completed: 'success',
  concluded: 'info',
  cancelled: 'error'
};

const ABTestExportModal = ({ open, onClose, test, token }) => {
  const [format, setFormat] = useState('json');
  const [includeVariantStats, setIncludeVariantStats] = useState(true);
  const [includePairwise, setIncludePairwise] = useState(true);
  const [includeRankings, setIncludeRankings] = useState(true);
  const [includeLLMAnalysis, setIncludeLLMAnalysis] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    if (!test?._id) return;
    setDownloading(true);
    setError(null);

    try {
      const response = await exportABTestResults(test._id, format, token);
      const testName = test.name?.replace(/[^a-z0-9]/gi, '-') || 'ab-test';
      const timestamp = Date.now();

      let blob;
      let extension;
      if (format === 'json') {
        blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
        extension = 'json';
      } else {
        blob = new Blob([typeof response === 'string' ? response : JSON.stringify(response, null, 2)], { type: 'text/markdown' });
        extension = 'md';
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${testName}-${timestamp}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      onClose();
    } catch (err) {
      console.error('Error exporting A/B test:', err);
      setError('Failed to export A/B test results. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const status = test?.status || 'unknown';
  const variantCount = test?.variants?.length || 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" component="h2" gutterBottom>
          Export A/B Test Results
        </Typography>

        {test && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {test.name}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={status.toUpperCase()}
                size="small"
                color={STATUS_COLORS[status] || 'default'}
              />
              <Chip
                label={`${variantCount} variants`}
                size="small"
                color="primary"
              />
              {test.conclusion?.winnerName && (
                <Chip
                  label={`Winner: ${test.conclusion.winnerName}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={format}
              label="Export Format"
              onChange={(e) => setFormat(e.target.value)}
            >
              <MenuItem value="json">
                <Box display="flex" alignItems="center" gap={1}>
                  JSON
                  <Typography variant="caption" color="text.secondary">
                    (for data analysis)
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="markdown">
                <Box display="flex" alignItems="center" gap={1}>
                  Markdown
                  <Typography variant="caption" color="text.secondary">
                    (readable report format)
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Divider />

          <Typography variant="subtitle1" fontWeight="bold">
            Content to Include
          </Typography>

          <FormControlLabel
            control={<Checkbox checked={includeVariantStats} onChange={(e) => setIncludeVariantStats(e.target.checked)} />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <StatsIcon fontSize="small" color="action" />
                Variant Statistics
                <Typography variant="caption" color="text.secondary">(run counts, averages, std dev)</Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Checkbox checked={includePairwise} onChange={(e) => setIncludePairwise(e.target.checked)} />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CompareIcon fontSize="small" color="action" />
                Pairwise Comparisons
                <Typography variant="caption" color="text.secondary">(p-values, effect sizes, CIs)</Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Checkbox checked={includeRankings} onChange={(e) => setIncludeRankings(e.target.checked)} />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <RankingsIcon fontSize="small" color="action" />
                Rankings
                <Typography variant="caption" color="text.secondary">(per-metric ranked tables)</Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={<Checkbox checked={includeLLMAnalysis} onChange={(e) => setIncludeLLMAnalysis(e.target.checked)} />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <AnalysisIcon fontSize="small" color="action" />
                LLM Comparative Analysis
                <Typography variant="caption" color="text.secondary">(AI-generated insights)</Typography>
              </Box>
            }
          />

          <Divider />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading ? 'Exporting...' : `Export as ${format.toUpperCase()}`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default ABTestExportModal;
